import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SessionBlockRow from "./workout-generator/SessionBlockRow";
import CuratedWorkoutChatPanel from "./workout-generator/CuratedWorkoutChatPanel";
import {
  addLineExerciseMapping,
  addWorkoutGeneratorBlockLine,
  aiGenerateWorkoutGeneratorLineExercise,
  assistantMessageCount,
  canGenerateWorkout,
  confirmCuratedPhaseWorkouts,
  finalizeLineMappings,
  generateCuratedPhaseWorkouts,
  getCuratedPhaseWorkoutsChat,
  getOrCreateWorkoutGeneratorSession,
  parseWorkoutGeneratorBlock,
  removeLineExerciseMapping,
  reopenLineMappings,
  resetWorkoutGeneratorSession,
  resolveWorkoutGeneratorLine,
  restartCuratedPhaseWorkouts,
  sendCuratedPhaseWorkoutsMessage,
  updateWorkoutGeneratorLineNameStatus,
  waitForAssistantResponse,
  type WorkoutGeneratorSessionPayload,
} from "@/integrations/backend/workout-generator";
import type { AIGenerationConversation } from "@/integrations/backend/program-generation";
import { loadExercisesWithRelations } from "@/integrations/backend/exercises";
import { isoDatePlusDays } from "@/lib/date-only";

type Props = {
  clientId: string;
  phaseId: string;
  onBack: () => void;
  onNavigateToWorkouts: () => void;
};

export default function WorkoutGeneratorPage({ clientId, phaseId, onBack, onNavigateToWorkouts }: Props) {
  const [payload, setPayload] = useState<WorkoutGeneratorSessionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [busyBlockId, setBusyBlockId] = useState<string | null>(null);
  const [parsingBlockIds, setParsingBlockIds] = useState<Set<string>>(new Set());
  const [generatingWorkout, setGeneratingWorkout] = useState(false);
  const [conversation, setConversation] = useState<AIGenerationConversation | null>(null);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [exerciseNameMap, setExerciseNameMap] = useState<Record<string, string>>({});

  const applyPayload = useCallback((next: WorkoutGeneratorSessionPayload) => {
    setPayload(next);
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrCreateWorkoutGeneratorSession(clientId, phaseId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to load workout generator");
    } finally {
      setLoading(false);
    }
  }, [applyPayload, clientId, phaseId]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    void loadExercisesWithRelations().then((exercises) => {
      const map: Record<string, string> = {};
      for (const ex of exercises) map[ex.id] = ex.name;
      setExerciseNameMap(map);
    });
  }, []);

  const parsePendingBlocks = useCallback(
    async (blocks: WorkoutGeneratorSessionPayload["blocks"]) => {
      const pending = blocks.filter((b) => b.parse_status === "pending");
      if (pending.length === 0) return;
      setParsingBlockIds(new Set(pending.map((b) => b.id)));
      for (const block of pending) {
        try {
          const updated = await parseWorkoutGeneratorBlock(clientId, phaseId, block.id);
          applyPayload(updated);
        } catch (error: unknown) {
          toast.error(error instanceof Error ? error.message : `Failed to parse block ${block.row_index + 1}`);
        }
      }
      setParsingBlockIds(new Set());
    },
    [applyPayload, clientId, phaseId]
  );

  const pendingBlockCount = useMemo(
    () => payload?.blocks.filter((b) => b.parse_status === "pending").length ?? 0,
    [payload?.blocks]
  );

  useEffect(() => {
    if (!payload || pendingBlockCount === 0) return;
    void parsePendingBlocks(payload.blocks);
  }, [payload?.session.id, pendingBlockCount, parsePendingBlocks]);

  const resumeChatIfNeeded = useCallback(async () => {
    if (!payload || payload.session.step !== "chat") return;
    const convId = payload.session.conversation_id;
    if (!convId) return;
    try {
      const chat = await getCuratedPhaseWorkoutsChat(clientId, phaseId, convId);
      setConversation(chat);
    } catch {
      /* ignore */
    }
  }, [clientId, phaseId, payload]);

  useEffect(() => {
    void resumeChatIfNeeded();
  }, [resumeChatIfNeeded]);

  const phaseHeader = useMemo(() => {
    if (!payload) return null;
    const weekStart = payload.session.week_start ?? payload.phase.week_start;
    const weekEnd = payload.session.week_end ?? payload.phase.week_end;
    const startDate =
      payload.session.phase1_start_date ?? payload.phase.phase1_start_date;
    const start = isoDatePlusDays(startDate, (weekStart - 1) * 7);
    const end = isoDatePlusDays(startDate, weekEnd * 7 - 1);
    const siblings = payload.phases?.length ? payload.phases : [payload.phase];
    const sessionLabels = siblings
      .map((p) => ("session_structure_name" in p ? p.session_structure_name : "") || p.title)
      .filter(Boolean);
    return {
      title: `Weeks ${weekStart}–${weekEnd}`,
      weeks: `${weekStart}–${weekEnd}`,
      dates: start && end ? `${start} → ${end}` : "",
      sessionCount: siblings.length,
      sessionLabels,
    };
  }, [payload]);

  const blocksBySection = useMemo(() => {
    if (!payload) return [];
    const sections: Array<{
      key: string;
      label: string;
      blocks: WorkoutGeneratorSessionPayload["blocks"];
    }> = [];
    const order: string[] = [];
    const map = new Map<string, WorkoutGeneratorSessionPayload["blocks"]>();
    for (const block of payload.blocks) {
      const key = block.program_phase_id || "unknown";
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(block);
    }
    for (const key of order) {
      const blocks = map.get(key) || [];
      const first = blocks[0];
      const label =
        first?.session_structure_name?.trim() ||
        first?.program_phase_title?.trim() ||
        (payload.phases || []).find((p) => p.id === key)?.session_structure_name ||
        (payload.phases || []).find((p) => p.id === key)?.title ||
        "Session";
      sections.push({ key, label, blocks });
    }
    return sections;
  }, [payload]);

  const handleInclude = async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      let data = await updateWorkoutGeneratorLineNameStatus(clientId, phaseId, lineId, "confirmed");
      applyPayload(data);
      data = await resolveWorkoutGeneratorLine(clientId, phaseId, lineId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to include exercise");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleExclude = async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await updateWorkoutGeneratorLineNameStatus(clientId, phaseId, lineId, "excluded");
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to exclude exercise");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleUndoInclude = async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await updateWorkoutGeneratorLineNameStatus(clientId, phaseId, lineId, "pending");
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to go back");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleAddPool = async (blockId: string, label?: string) => {
    setBusyBlockId(blockId);
    try {
      const data = await addWorkoutGeneratorBlockLine(clientId, phaseId, blockId, label);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to add exercise pool");
    } finally {
      setBusyBlockId(null);
    }
  };

  const handleAddExercise = async (lineId: string, exerciseId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await addLineExerciseMapping(clientId, phaseId, lineId, exerciseId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to add exercise");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleRemoveExercise = async (lineId: string, mappingId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await removeLineExerciseMapping(clientId, phaseId, lineId, mappingId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to remove exercise");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleFinalize = async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await finalizeLineMappings(clientId, phaseId, lineId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to finalize mappings");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleReopen = async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await reopenLineMappings(clientId, phaseId, lineId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to reopen mappings");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleAiGenerate = async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      const data = await aiGenerateWorkoutGeneratorLineExercise(clientId, phaseId, lineId);
      applyPayload(data);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to create exercise with AI");
    } finally {
      setBusyLineId(null);
    }
  };

  const handleGenerateWorkout = async () => {
    if (!payload || payload.session.stale) return;
    setGeneratingWorkout(true);
    setChatBusy(true);
    try {
      const conv = await generateCuratedPhaseWorkouts(clientId, phaseId, payload.session.id);
      setConversation(conv);
      applyPayload({ ...payload, session: { ...payload.session, step: "chat", conversation_id: conv.conversation_id } });
      await waitForAssistantResponse({
        conversationId: conv.conversation_id,
        initialAssistantCount: assistantMessageCount(conv),
        fetchConversation: (conversationId) => getCuratedPhaseWorkoutsChat(clientId, phaseId, conversationId),
        applyConversation: setConversation,
      });
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to generate workouts");
    } finally {
      setGeneratingWorkout(false);
      setChatBusy(false);
    }
  };

  const handleSendChat = async () => {
    if (!conversation?.conversation_id || !chatInput.trim()) return;
    setChatBusy(true);
    try {
      const conv = await sendCuratedPhaseWorkoutsMessage(clientId, phaseId, {
        conversation_id: conversation.conversation_id,
        content: chatInput.trim(),
      });
      setChatInput("");
      setConversation(conv);
      await waitForAssistantResponse({
        conversationId: conv.conversation_id,
        initialAssistantCount: assistantMessageCount(conv),
        fetchConversation: (conversationId) => getCuratedPhaseWorkoutsChat(clientId, phaseId, conversationId),
        applyConversation: setConversation,
      });
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to send message");
    } finally {
      setChatBusy(false);
    }
  };

  const handleRestartChat = async () => {
    setChatBusy(true);
    try {
      const conv = await restartCuratedPhaseWorkouts(clientId, phaseId);
      setConversation(conv);
      await waitForAssistantResponse({
        conversationId: conv.conversation_id,
        initialAssistantCount: assistantMessageCount(conv),
        fetchConversation: (conversationId) => getCuratedPhaseWorkoutsChat(clientId, phaseId, conversationId),
        applyConversation: setConversation,
      });
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to restart");
    } finally {
      setChatBusy(false);
    }
  };

  const handleConfirmWorkouts = async () => {
    if (!conversation?.conversation_id) return;
    setChatBusy(true);
    try {
      await confirmCuratedPhaseWorkouts(clientId, phaseId, conversation.conversation_id);
      toast.success("Workouts saved");
      onNavigateToWorkouts();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to save workouts");
    } finally {
      setChatBusy(false);
    }
  };

  const handleResetSession = async () => {
    try {
      const data = await resetWorkoutGeneratorSession(clientId, phaseId);
      applyPayload(data);
      setConversation(null);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to reset session");
    }
  };

  if (loading || !payload) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading workout generator…
      </div>
    );
  }

  const step = payload.session.step;
  const ready = canGenerateWorkout(payload.blocks);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2 h-9 gap-1.5 px-2 text-sm font-medium text-gray-800 hover:bg-gray-100 hover:text-gray-950"
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            Back to program phases
          </Button>
          <h2 className="text-2xl font-bold text-gray-900">Workout generator</h2>
          {phaseHeader ? (
            <div className="space-y-1">
              <p className="text-sm text-gray-600">
                {phaseHeader.title}
                {phaseHeader.dates ? ` · ${phaseHeader.dates}` : ""}
                {phaseHeader.sessionCount > 1
                  ? ` · ${phaseHeader.sessionCount} session types`
                  : ""}
              </p>
              {phaseHeader.sessionLabels.length > 0 ? (
                <p className="text-xs text-gray-500">
                  {phaseHeader.sessionLabels.join(" · ")}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {payload.session.stale ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300/80 bg-amber-50 px-4 py-3 text-sm text-slate-800">
          <span className="font-medium">
            This session is out of date with the outer phase. Reset before generating workouts.
          </span>
          <Button type="button" size="sm" variant="outline" onClick={() => void handleResetSession()}>
            Reset session
          </Button>
        </div>
      ) : null}

      {payload.session.stale ? null : step === "chat" ? (
        <CuratedWorkoutChatPanel
          conversation={conversation}
          input={chatInput}
          onInputChange={setChatInput}
          busy={chatBusy}
          onSend={() => void handleSendChat()}
          onRestart={() => void handleRestartChat()}
          onConfirm={() => void handleConfirmWorkouts()}
          onBack={() =>
            applyPayload({ ...payload, session: { ...payload.session, step: "curation" } })
          }
          exerciseNameMap={exerciseNameMap}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-3 py-2 font-semibold text-gray-700 w-1/5">Session block</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 w-2/5">Exercise / series</th>
                  <th
                    className="px-3 py-2 font-semibold text-gray-700 w-2/5"
                    title="Catalog exercises the generator may choose from for this session block"
                  >
                    Generator exercise pool
                  </th>
                </tr>
              </thead>
              <tbody>
                {payload.blocks.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-gray-500">
                      No session block rows found in this outer phase.
                    </td>
                  </tr>
                ) : (
                  blocksBySection.map((section) => (
                    <Fragment key={section.key}>
                      <tr className="border-b border-gray-200 bg-gray-100/80">
                        <td colSpan={3} className="px-3 py-2 text-sm font-semibold text-gray-900">
                          {section.label}
                        </td>
                      </tr>
                      {section.blocks.map((block) => (
                        <SessionBlockRow
                          key={block.id}
                          block={block}
                          busyLineId={busyLineId}
                          busyBlock={busyBlockId === block.id}
                          parsing={parsingBlockIds.has(block.id)}
                          onInclude={handleInclude}
                          onExclude={handleExclude}
                          onUndoInclude={handleUndoInclude}
                          onAddExercise={handleAddExercise}
                          onRemoveExercise={handleRemoveExercise}
                          onFinalize={handleFinalize}
                          onReopen={handleReopen}
                          onAiGenerate={handleAiGenerate}
                          onAddPool={handleAddPool}
                        />
                      ))}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end border-t border-gray-200 pt-4">
            <Button
              type="button"
              size="lg"
              disabled={!ready || generatingWorkout || payload.session.stale}
              onClick={() => void handleGenerateWorkout()}
            >
              {generatingWorkout ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating…
                </>
              ) : (
                "Generate workout"
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
